import { Layout, Tabs, Typography, Empty, Spin, Flex } from 'antd';
import React, { lazy, Suspense, useState, useRef, useMemo, memo, useCallback } from 'react';
import './style.css';
import { DoubleLeftOutlined, PlusOutlined } from '@ant-design/icons';
import InlineSearchUI from '../InlineSearchUI/InlineSearchUI';
import WorkspaceList from '../WorkspaceList';
import { WORKSPACE_TYPE_MAP } from '../../utils/constants';

// Lazy load components to improve performance
const TypedWorkspaceList = lazy(() => import('../TypedWorkspaceList'));
const SearchResultList = lazy(() => import('../SearchResultList/SearchResultList'));
const { Sider } = Layout;
const { Title } = Typography;

const PAGE_SIZE = 20;
// Memoize tab content to prevent unnecessary rerenders
const MemoizedWorkspaceList = memo(WorkspaceList);
const MemoizedTypedWorkspaceList = memo(({ type, ...props }) => (
  <Suspense
    fallback={
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="small" />
      </div>
    }
  >
    <TypedWorkspaceList type={type} {...props} />
  </Suspense>
));

const MemoizedSearchResultList = memo(({ results, onLoadMore, isLoading, ...props }) => (
  <Suspense
    fallback={
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="small" />
      </div>
    }
  >
    <SearchResultList results={results} onLoadMore={onLoadMore} isLoading={isLoading} {...props} />
  </Suspense>
));

const Sidebar = memo(
  ({
    searchTerm,
    setSearchTerm,
    triggerSearch,
    searchQueryResult,
    isCollaspse,
    setIsCollaspse,
    popoverMenuContent,
    activeTab,
    loadedTabs,
    handleTabChange,
    prevRef,
    setOpenWorkspaceCreateModal,
  }) => {
    const sidebarRef = useRef(null);
    // const [activeTab, setActiveTab] = useState('1');
    // const prevRef = useRef({ term: '', type: WORKSPACE_TYPE_MAP[activeTab], page: 0 });
    // const [loadedTabs, setLoadedTabs] = useState(['1']); // Start with 'All' tab already loaded
    // const [triggerSearch, searchQueryResult] = useLazyFetchSearchResultsQuery();
    // const [searchTerm, setSearchTerm] = useState('');
    const { data, isFetching, isLoading, reset } = searchQueryResult ?? {};
    // Calculate the left position based on leftbar state

    // // Function to handle tab change
    // const handleTabChange = useCallback((key) => {
    //     setActiveTab(key);
    //     // Add this tab to our loaded tabs if it's not already there
    //     if (!loadedTabs.includes(key)) {
    //         setLoadedTabs(prev => [...prev, key]);
    //     }
    // }, [loadedTabs]);

    const loadMoreFn = () => {
      if (!searchTerm.trim() || isFetching || isLoading || prevRef?.current?.page * PAGE_SIZE > data?.totalWorkspaces)
        return;
      const page = prevRef?.current?.page + 1;
      const active = prevRef?.type;
      prevRef.current = {
        ...prevRef?.current,
        page,
      };
      triggerSearch({
        query: searchTerm,
        size: page * PAGE_SIZE,
        from: 0,
        searchType: 'workspaces',
        filterType: active === 'All' ? '' : active,
      });
    };

    // Only render the content for tabs that have been activated at least once
    const getTabChildren = useCallback(
      (key, type) => {
        if (!loadedTabs.includes(key)) return <Spin size="small" />;

        // If searching, show filtered results for the current tab
        if (searchTerm) {
          return data?.workspaces?.length > 0 ? (
            <MemoizedSearchResultList
              results={data?.workspaces}
              totalWorkspaces={data?.totalWorkspaces}
              popoverMenuContent={popoverMenuContent}
              setIsCollaspse={setIsCollaspse}
              onLoadMore={key === activeTab ? loadMoreFn : null} // Only enable infinite scroll on active tab
              isLoading={isFetching}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No matching workspaces found"
              className="search-empty-state"
            />
          );
        }

        // If not searching, show normal workspace list with appropriate infinite scroll
        if (key === '1') {
          return (
            <MemoizedWorkspaceList
              popoverMenuContent={popoverMenuContent}
              setIsCollaspse={setIsCollaspse}
              enableInfiniteScroll={true}
            />
          );
        } else {
          return (
            <MemoizedTypedWorkspaceList
              type={type}
              popoverMenuContent={popoverMenuContent}
              setIsCollaspse={setIsCollaspse}
              enableInfiniteScroll={true}
            />
          );
        }
      },
      [loadedTabs, searchTerm, activeTab, loadMoreFn, isFetching, popoverMenuContent, setIsCollaspse, data]
    );

    // Tab definitions - use useMemo to prevent recreating on every render
    const items = useMemo(
      () => [
        {
          key: '1',
          label: 'All',
          children: getTabChildren('1'),
        },
        {
          key: '2',
          label: 'Service',
          children: getTabChildren('2', 'SERVICE'),
        },
        {
          key: '3',
          label: 'Team',
          children: getTabChildren('3', 'TEAM'),
        },
        {
          key: '4',
          label: 'Project',
          children: getTabChildren('4', 'PROJECT'),
        },
        {
          key: '5',
          label: 'Private',
          children: getTabChildren('5', 'Private'),
        },
        {
          key: '6',
          label: 'Others',
          children: getTabChildren('6', 'OTHERS'),
        },
      ],
      [getTabChildren]
    );

    return (
      <>
        <Sider
          width={480}
          className="workspace-sidebar"
          theme="light"
          style={{
            background: '#f5f5f5',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
          }}
          collapsed={isCollaspse}
          ref={sidebarRef}
        >
          <div className="sidebar-content">
            <div className="sidebar-content-header">
              <Title style={{ marginTop: 0 }} level={5}>
                Workspaces
              </Title>
              <div className="sidebar-content-right">
                <DoubleLeftOutlined style={{ cursor: 'pointer' }} onClick={() => setIsCollaspse(true)} />
                <PlusOutlined style={{ cursor: 'pointer' }} onClick={() => setOpenWorkspaceCreateModal(true)} />
              </div>
            </div>
            <InlineSearchUI
              activeTabType={WORKSPACE_TYPE_MAP[activeTab]}
              triggerSearch={triggerSearch}
              isFetching={isFetching}
              isLoading={isLoading}
              reset={reset}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              prevRef={prevRef}
            />
            <Spin spinning={isFetching}>
              <Tabs
                className="sidebar-tabs"
                defaultActiveKey="1"
                activeKey={activeTab}
                onChange={handleTabChange}
                items={items}
                centered
                tabBarStyle={{
                  borderBottom: '1px solid #d9d9d9',
                }}
                indicator={{ size: 0 }}
              />
            </Spin>
          </div>
        </Sider>
      </>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
